<script lang="ts">
  import {createEventDispatcher} from 'svelte';
  import InlineSVG from 'svelte-inline-svg';

  import {
    Confidentiality,
    Visibility,
    DiamondSector,
    ProjectType
  } from '../../types/project';

  import {
    customers,
    teamAndProjectLeads,
    fields,
    topics,
    services,
    tasks,
    technologies,
    formats,
    methods
  } from '../../stores/projects';
  import {
    typeFilter,
    confidentialityFilter,
    visibilityFilter,
    diamondFilter,
    customersFilter,
    teamFilter,
    fieldsFilter,
    topicsFilter,
    servicesFilter,
    showDrafts,
    startDateFilter,
    endDateFilter,
    tasksFilter,
    technologiesFilter,
    minValueInTimeFilter,
    maxValueInTimeFilter,
    minValueInPriceFilter,
    maxValueInPriceFilter,
    formatsFilter,
    methodsFilter
  } from '../../stores/filter-and-sort';
  import {user} from '../../stores/user';

  import resetIcon from '../../assets/icons/reset.svg';

  import IconButton from '../icon-button/IconButton.svelte';
  import ToggleButtonGroup from '../toggle-button-group/ToggleButtonGroup.svelte';
  import DatePicker from '../date-picker/DatePicker.svelte';
  import Dropdown from '../dropdown/Dropdown.svelte';
  import NumberRangeInputGroup from '../number-range-input-group/NumberRangeInputGroup.svelte';
  import Checkbox from '../checkbox/Checkbox.svelte';

  const dispatch = createEventDispatcher();

  export let numberOfProjectsTotal: number;
  export let numberOfProjectsFiltered: number;

  const UBILABS_INCORPORATION_DATE = new Date('2007-03-22');

  const typeOptions = [
    {value: null, label: 'Filter all projects'},
    {value: ProjectType.DEVELOPMENT, label: 'Show only development projects'},
    {value: ProjectType.CONSULTING, label: 'Show only consulting projects'}
  ];
</script>

<style lang="postcss">
  .filtersHeader {
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .filtersHeader h3 {
    min-width: 10.5rem;
    margin: 0 0.25rem 0 0;
  }

  .filterGroups {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin: 0 -1rem -0.5rem;
    padding: 0 1rem 1rem;
  }

  @media (min-width: 600px) {
    .filterGroups {
      display: grid;
      grid-template-columns: repeat(2, var(--ubilabs-input-width));
    }
  }

  @media (min-width: 600px) and (max-width: 935px) {
    .group.general {
      grid-row: 2 / 4;
    }
  }

  @media (min-width: 936px) {
    .filterGroups {
      grid-template-columns: repeat(3, var(--ubilabs-input-width));
      column-gap: 2rem;
    }
  }

  @media (min-width: 1216px) {
    .filterGroups {
      grid-template-columns: calc(var(--ubilabs-input-width) * 2 + 1rem) repeat(
          2,
          var(--ubilabs-input-width)
        );
    }

    .group.general > div {
      display: inline-block;
      column-count: 2;
    }
  }

  @media (min-width: 1248px) {
    .filterGroups {
      column-gap: 3rem;
    }
  }

  @media (max-width: 935px) {
    .group.consulting {
      grid-column: 2;
    }
  }

  label {
    display: flex;
    margin-bottom: 0.25rem;
  }

  .filter:not(:first-of-type) {
    break-inside: avoid;
    margin-top: 1rem;
  }

  :global(.draftsFilter) {
    min-height: var(--ubilabs-input-height);
  }

  .group.disabled h4,
  .group.disabled label {
    color: var(--ubilabs-gray-dark-2);
  }

  .badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.5em;
    height: 1.5em;
    margin: 0.0625rem 0.25rem 0;
    padding: 0.1875rem 0.25rem 0.25rem;
    border-radius: 0.75em;
    background-color: var(--ubilabs-blue);
    color: var(--ubilabs-white);
    font-size: 0.75em;
  }

  .group.disabled .badge {
    background-color: var(--ubilabs-gray-dark-1);
  }

  .projectTypeFilter {
    grid-column: 1 / 3;
  }

  @media (min-width: 936px) {
    .projectTypeFilter {
      grid-column: 1 / 4;
    }
  }

  :global(.filterInputGroup) {
    display: inline-block;
    width: 100%;
    max-width: var(--ubilabs-input-width);
  }

  :global(.filterInputGroup.horizontal) {
    display: grid;
    grid-template-columns: auto 1fr auto 1fr;
    gap: 0.25rem;
    align-items: center;
  }

  :global(.filterInputGroup.horizontal > input) {
    min-width: 0;
    width: auto;
  }

  :global(.filterInputGroup.horizontal > label) {
    margin: 0;
  }
  :global(.filterInputGroup.horizontal > label:not(:first-child)) {
    margin-left: 0.25rem;
  }
</style>

<div>
  <div class="filtersHeader">
    <h3>
      Filter projects ({numberOfProjectsFiltered} / {numberOfProjectsTotal})
    </h3>
    <IconButton
      class="resetButton"
      title="Reset filters"
      aria-label="Reset filters"
      disabled={numberOfProjectsFiltered === numberOfProjectsTotal}
      on:click={() => dispatch('resetFilters')}>
      <InlineSVG src={resetIcon} />
    </IconButton>
  </div>

  <div class="filterGroups">
    <div class="projectTypeFilter">
      <label for="projectTypeFilter">Project type</label>
      <ToggleButtonGroup
        name="projectTypeFilter"
        options={typeOptions}
        bind:value={$typeFilter} />
    </div>

    <div class="group general">
      <h4>General filters</h4>

      <div>
        {#if $user}
          <div class="filter">
            <!-- svelte-ignore a11y-label-has-associated-control -->
            <!-- eslint-disable-next-line a11y-label-has-associated-control -->
            <label>Project Drafts</label>
            <Checkbox
              class="draftsFilter"
              id="draftsFilter"
              value="showDrafts"
              bind:checked={$showDrafts}>
              Show project drafts
            </Checkbox>
          </div>

          <div class="filter">
            <label for="confidentialityFilter">
              Confidentiality
              {#if $confidentialityFilter}
                <span class="badge" />
              {/if}
            </label>
            <Dropdown
              id="confidentialityFilter"
              name="confidentialityFilter"
              bind:value={$confidentialityFilter}
              options={Object.values(Confidentiality).map(confidentiality => ({
                value: confidentiality,
                label: confidentiality
              }))}
              on:change={({detail}) => {
                if (
                  detail === Confidentiality.CONFIDENTIAL &&
                  $visibilityFilter === Visibility.PUBLIC
                ) {
                  visibilityFilter.reset();
                }
              }} />
          </div>

          <div class="filter">
            <label for="visibilityFilter">
              Visibility
              {#if $visibilityFilter}
                <span class="badge" />
              {/if}
            </label>
            <Dropdown
              id="visibilityFilter"
              name="visibilityFilter"
              bind:value={$visibilityFilter}
              options={Object.values(Visibility).map(visibility => ({
                value: visibility,
                label: visibility
              }))}
              disabled={$confidentialityFilter ===
                Confidentiality.CONFIDENTIAL} />
          </div>
        {/if}

        <div class="filter">
          <label for="creationPeriodStart">Creation period</label>
          <div class="filterInputGroup">
            <DatePicker
              label="from:"
              min={UBILABS_INCORPORATION_DATE}
              max={$endDateFilter || new Date()}
              closeOnSelect={false}
              bind:value={$startDateFilter}
              id="creationPeriodStart" />

            <DatePicker
              label="to:"
              min={$startDateFilter || UBILABS_INCORPORATION_DATE}
              max={new Date()}
              closeOnSelect={false}
              bind:value={$endDateFilter}
              id="creationPeriodEnd" />
          </div>
        </div>

        <div class="filter">
          <label for="customersFilter">
            Customers
            {#if $customersFilter.length}
              <span class="badge">{$customersFilter.length}</span>
            {/if}
          </label>
          <Dropdown
            id="customersFilter"
            name="customersFilter"
            bind:value={$customersFilter}
            options={$customers.map(customer => ({
              value: customer,
              label: customer
            }))}
            multiple />
        </div>

        <div class="filter">
          <label for="teamFilter">
            Team
            {#if $teamFilter.length}
              <span class="badge">{$teamFilter.length}</span>
            {/if}
          </label>
          <Dropdown
            id="teamFilter"
            name="teamFilter"
            bind:value={$teamFilter}
            options={$teamAndProjectLeads.map(teamMember => ({
              value: teamMember,
              label: teamMember
            }))}
            multiple />
        </div>

        <div class="filter">
          <label for="diamondFilter">
            Diamond
            {#if $diamondFilter.length}
              <span class="badge">{$diamondFilter.length}</span>
            {/if}
          </label>
          <Dropdown
            id="diamondFilter"
            name="diamondFilter"
            bind:value={$diamondFilter}
            options={Object.values(DiamondSector).map(sector => ({
              value: sector,
              label: sector
            }))}
            multiple />
        </div>

        <div class="filter">
          <label for="fieldsFilter">
            Verticals / Industries
            {#if $fieldsFilter.length}
              <span class="badge">{$fieldsFilter.length}</span>
            {/if}
          </label>
          <Dropdown
            id="fieldsFilter"
            name="fieldsFilter"
            bind:value={$fieldsFilter}
            options={$fields.map(field => ({
              value: field,
              label: field
            }))}
            multiple />
        </div>

        <div class="filter">
          <label for="topicsFilter">
            Focus / Topic
            {#if $topicsFilter.length}
              <span class="badge">{$topicsFilter.length}</span>
            {/if}
          </label>
          <Dropdown
            id="topicsFilter"
            name="topicsFilter"
            bind:value={$topicsFilter}
            options={$topics.map(topic => ({
              value: topic,
              label: topic
            }))}
            multiple />
        </div>

        <div class="filter">
          <label for="servicesFilter">
            Services
            {#if $servicesFilter.length}
              <span class="badge">{$servicesFilter.length}</span>
            {/if}
          </label>
          <Dropdown
            id="servicesFilter"
            name="servicesFilter"
            bind:value={$servicesFilter}
            options={$services.map(service => ({
              value: service,
              label: service
            }))}
            multiple />
        </div>
      </div>
    </div>

    <div
      class="group development {$typeFilter === ProjectType.CONSULTING &&
        'disabled'}">
      <h4>Development projects filters</h4>

      <div class="filter">
        <label for="tasksFilter">
          Use cases
          {#if $tasksFilter.length}
            <span class="badge">{$tasksFilter.length}</span>
          {/if}
        </label>
        <Dropdown
          id="tasksFilter"
          name="tasksFilter"
          bind:value={$tasksFilter}
          options={$tasks.map(task => ({
            value: task,
            label: task
          }))}
          multiple
          disabled={$typeFilter === ProjectType.CONSULTING} />
      </div>

      <div class="filter">
        <label for="technologiesFilter">
          Technologies
          {#if $technologiesFilter.length}
            <span class="badge">{$technologiesFilter.length}</span>
          {/if}
        </label>
        <Dropdown
          id="technologiesFilter"
          name="technologiesFilter"
          bind:value={$technologiesFilter}
          options={$technologies.map(technology => ({
            value: technology,
            label: technology
          }))}
          multiple
          disabled={$typeFilter === ProjectType.CONSULTING} />
      </div>
    </div>

    <div
      class="group consulting {$typeFilter === ProjectType.DEVELOPMENT &&
        'disabled'}">
      <h4>Consulting projects filters</h4>

      <div class="filter">
        <label for="formatsFilter">
          Formats
          {#if $formatsFilter.length}
            <span class="badge">{$formatsFilter.length}</span>
          {/if}
        </label>
        <Dropdown
          id="formatsFilter"
          name="formatsFilter"
          bind:value={$formatsFilter}
          options={$formats.map(format => ({
            value: format,
            label: format
          }))}
          multiple
          disabled={$typeFilter === ProjectType.DEVELOPMENT} />
      </div>

      <div class="filter">
        <label for="methodsFilter">
          Methods
          {#if $methodsFilter.length}
            <span class="badge">{$methodsFilter.length}</span>
          {/if}
        </label>
        <Dropdown
          id="methodsFilter"
          name="methodsFilter"
          bind:value={$methodsFilter}
          options={$methods.map(method => ({
            value: method,
            label: method
          }))}
          multiple
          disabled={$typeFilter === ProjectType.DEVELOPMENT} />
      </div>

      <div class="filter">
        <label for="valueInTimeFilter-min">
          Value in Time (Hours)
          {#if $minValueInTimeFilter != null && $maxValueInTimeFilter != null}
            <span class="badge" />
          {/if}
        </label>
        <NumberRangeInputGroup
          class="filterInputGroup horizontal"
          id="valueInTimeFilter"
          bind:minValue={$minValueInTimeFilter}
          bind:maxValue={$maxValueInTimeFilter}
          disabled={$typeFilter === ProjectType.DEVELOPMENT} />
      </div>

      <div class="filter">
        <label for="minValueInPriceFilter">
          Value in Price (Euros)
          {#if $minValueInPriceFilter != null && $maxValueInPriceFilter != null}
            <span class="badge" />
          {/if}
        </label>
        <NumberRangeInputGroup
          class="filterInputGroup horizontal"
          id="valueInPriceFilter"
          bind:minValue={$minValueInPriceFilter}
          bind:maxValue={$maxValueInPriceFilter}
          disabled={$typeFilter === ProjectType.DEVELOPMENT} />
      </div>
    </div>
  </div>
</div>
